import React, { useState, useEffect } from "react";
import { countries } from "../../store/countries"; // Assuming you have this import
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Unauthorized from "../../components/pages/Unauthorized";
import { useAuth } from "../../components/auth/AuthContext";
import { useNavigate, Outlet, useLocation } from "react-router-dom"; // Import Outlet and useLocation
import { SERVER_URL } from "./SERVER_URL";
import { FaArrowLeftLong } from "react-icons/fa6";
import LogicViewer from "./LogicViewer";

const QualitativeAssessmentDashboard = () => {
  const { authState } = useAuth();
  const user = authState.user;

  const [filters, setFilters] = useState({
    country: "",
    category: "",
    subCategory: "",
    year: "",
  });
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingForms, setIsLoadingForms] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableFormData, setEditableFormData] = useState({
    category: "",
    sub_category: "",
    year: "",
    questions: [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [editHeaders, setEditHeaders] = useState([]); // headers available while editing
  const [newEditHeaderName, setNewEditHeaderName] = useState("");
  const [logicIsOpen, setLogicIsOpen] = useState(false);
  const [logicLoading, setLogicLoading] = useState(false);
  const [logicQuestions, setLogicQuestions] = useState([]);

  const removeEditHeader = (targetHeader) => {
    setEditHeaders((prev) => prev.filter((h) => {
      if (targetHeader.id != null) return String(h.id) !== String(targetHeader.id);
      return (h.name || "") !== (targetHeader.name || "");
    }));
    setEditableFormData((prev) => {
      const updated = { ...prev };
      updated.questions = (prev.questions || []).map((q) => {
        const matchesId = targetHeader.id != null && String(q.headerId) === String(targetHeader.id);
        const matchesName = targetHeader.name && q.headerName === targetHeader.name;
        return matchesId || matchesName ? { ...q, headerId: null, headerName: null } : q;
      });
      return updated;
    });
  };

  const assignRemainingQuestionsToHeader = (targetHeader) => {
    const headerIdToUse = targetHeader.id != null ? targetHeader.id : Date.now();
    const headerNameToUse = targetHeader.name || (targetHeader.id ? `Header ${targetHeader.id}` : "Header");

    setEditHeaders((prev) =>
      prev.map((h) => {
        const matchesId = targetHeader.id != null && String(h.id) === String(targetHeader.id);
        const matchesName = targetHeader.name && (h.name || "") === targetHeader.name;
        if (matchesId || matchesName) {
          return { ...h, id: headerIdToUse, name: headerNameToUse };
        }
        return h;
      })
    );

    setEditableFormData((prev) => {
      const updated = { ...prev };
      updated.questions = (prev.questions || []).map((q) => {
        const unassigned = !q.headerId && !q.headerName;
        return unassigned
          ? { ...q, headerId: headerIdToUse, headerName: headerNameToUse }
          : q;
      });
      return updated;
    });
  };
  const navigate = useNavigate();
  const location = useLocation(); // Get the current location

  // Categories and sub-categories (shared with Create Form)
  const categories = {
    WASH: [
      "WASH Committee",
      "Community",
      "Adolescent Girls",
      "Health Officials",
    ],
    "Food Security": ["Student", "Food Distribution", "Farmers"],
    Education: ["Principal", "Orphan Students", "Teacher", "Parent Guardian"],
    Emergencies: ["EWS member v5", "Community of Disaster hit areas"],
    Health: [
      "Midwife",
      "Community(Women)",
      "Health Officials",
      "Community",
      "Medical Camp Beneficiary",
    ],
    Livelihood: ["Farmer", "Trainee"],
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFilters({ country: "", category: "", subCategory: "", year: "" });
    toast.info("Filters have been reset.");
  };

  const handleCreateForm = () => {
    navigate("/qualitative-assessment-dashboard/create-qa-form");
  };

  const handleManagePage = () => {
    navigate("/qualitative-assessment-dashboard/manage-page");
  };

  const handleViewData = () => {
    navigate("/qualitative-assessment-dashboard/view-data");
  };

  // Fetch all forms on component mount
  useEffect(() => {
    fetchAllForms();
  }, []);

  // Fetch forms based on filters
  useEffect(() => {
    if (filters.category && filters.subCategory && filters.year) {
      fetchFilteredForms();
    }
  }, [filters]);

  const fetchAllForms = async () => {
    setIsLoadingForms(true);
    try {
      const response = await fetch(`${SERVER_URL}/get_forms`);
      if (!response.ok) {
        throw new Error("Failed to fetch forms");
      }
      const data = await response.json();
      setForms(data);
    } catch (error) {
      console.error("Error fetching forms:", error);
      toast.error("Failed to load forms. Please try again.");
    } finally {
      setIsLoadingForms(false);
    }
  };

  const fetchFilteredForms = async () => {
    setIsLoadingForms(true);
    try {
      const response = await fetch(
        `${SERVER_URL}/get_forms?category=${filters.category}&sub_category=${filters.subCategory}&year=${filters.year}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch filtered forms");
      }
      const data = await response.json();
      setForms(data);
    } catch (error) {
      console.error("Error fetching filtered forms:", error);
      toast.error("Failed to load filtered forms. Please try again.");
    } finally {
      setIsLoadingForms(false);
    }
  };

  const handleShowQuestionnaire = async (form) => {
    setSelectedForm(form);
    setLoading(true);
    try {
      const response = await fetch(
        `${SERVER_URL}/get_questions?category=${form.category}&sub_category=${form.sub_category}&year=${form.year}`
      );
      const data = await response.json();
      setQuestions(data);
      setModalIsOpen(true);
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error("Failed to load questionnaire");
    } finally {
      setLoading(false);
    }
  };

  const handleViewLogic = async (form) => {
    setSelectedForm(form);
    setLogicLoading(true);
    try {
      const response = await fetch(
        `${SERVER_URL}/get_questions?category=${form.category}&sub_category=${form.sub_category}&year=${form.year}`
      );
      const data = await response.json();
      setLogicQuestions(data || []);
      setLogicIsOpen(true);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load logic view");
    } finally {
      setLogicLoading(false);
    }
  };

  const handleEditForm = async (form) => {
    setSelectedForm(form);
    setLoading(true);
    try {
      const response = await fetch(
        `${SERVER_URL}/get_questions?category=${form.category}&sub_category=${form.sub_category}&year=${form.year}`
      );
      const data = await response.json();
      const mappedQuestions = (data || []).map((q) => ({
        id: q.id,
        text: q.question_text,
        type: q.question_type,
        headerId: q.headerId ?? null,
        headerName: q.headerName ?? null,
        answers: (q.answers || []).map((a) => ({
          id: a.id,
          text: a.answer_text,
          next_question_id: a.next_question_id ?? null,
        })),
      }));
      setEditableFormData({
        category: form.category,
        sub_category: form.sub_category,
        year: String(form.year || ""),
        questions: mappedQuestions,
      });
      // build unique headers list for edit mode
      const headerMap = new Map();
      mappedQuestions.forEach((q, idx) => {
        if (q.headerId || q.headerName) {
          const key = String(q.headerId ?? q.headerName);
          if (!headerMap.has(key)) {
            const genId = q.headerId ?? (Date.now() + idx);
            headerMap.set(key, {
              id: genId,
              name: q.headerName ?? (q.headerId ? `Header ${q.headerId}` : "Header"),
            });
          }
        }
      });
      // ensure every question has a headerId if it had only name
      const withIds = mappedQuestions.map((q, idx) => {
        if (!q.headerId && q.headerName) {
          const key = String(q.headerName);
          const hdr = headerMap.get(key);
          return { ...q, headerId: hdr?.id ?? (Date.now() + 1000 + idx) };
        }
        return q;
      });
      setEditableFormData({
        category: form.category,
        sub_category: form.sub_category,
        year: String(form.year || ""),
        questions: withIds,
      });
      setEditHeaders(Array.from(headerMap.values()));
      setIsEditMode(true);
      setModalIsOpen(true);
    } catch (error) {
      console.error("Error fetching questions for edit:", error);
      toast.error("Failed to load form for editing");
    } finally {
      setLoading(false);
    }
  };

  const updateEditableQuestionText = (index, newText) => {
    setEditableFormData((prev) => {
      const updated = { ...prev };
      updated.questions = [...updated.questions];
      updated.questions[index] = { ...updated.questions[index], text: newText };
      return updated;
    });
  };

  const updateEditableQuestionType = (index, newType) => {
    setEditableFormData((prev) => {
      const updated = { ...prev };
      updated.questions = [...updated.questions];
      updated.questions[index] = { ...updated.questions[index], type: newType };
      return updated;
    });
  };

  const addEditableQuestion = () => {
    setEditableFormData((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        { id: undefined, text: "", type: "text", answers: [] },
      ],
    }));
  };

  const removeEditableQuestion = (index) => {
    setEditableFormData((prev) => {
      const updated = { ...prev };
      updated.questions = prev.questions.filter((_, i) => i !== index);
      return updated;
    });
  };

  const addEditableAnswer = (qIndex) => {
    setEditableFormData((prev) => {
      const updated = { ...prev };
      updated.questions = [...updated.questions];
      const q = { ...updated.questions[qIndex] };
      q.answers = [...(q.answers || []), { id: undefined, text: "", next_question_id: null }];
      updated.questions[qIndex] = q;
      return updated;
    });
  };

  const updateEditableAnswerText = (qIndex, aIndex, text) => {
    setEditableFormData((prev) => {
      const updated = { ...prev };
      updated.questions = [...updated.questions];
      const q = { ...updated.questions[qIndex] };
      const answers = [...(q.answers || [])];
      answers[aIndex] = { ...answers[aIndex], text };
      q.answers = answers;
      updated.questions[qIndex] = q;
      return updated;
    });
  };

  const updateEditableAnswerNextId = (qIndex, aIndex, value) => {
    setEditableFormData((prev) => {
      const updated = { ...prev };
      updated.questions = [...updated.questions];
      const q = { ...updated.questions[qIndex] };
      const answers = [...(q.answers || [])];
      const nextId = value === "" ? null : Number(value);
      answers[aIndex] = { ...answers[aIndex], next_question_id: isNaN(nextId) ? null : nextId };
      q.answers = answers;
      updated.questions[qIndex] = q;
      return updated;
    });
  };

  const removeEditableAnswer = (qIndex, aIndex) => {
    setEditableFormData((prev) => {
      const updated = { ...prev };
      updated.questions = [...updated.questions];
      const q = { ...updated.questions[qIndex] };
      q.answers = (q.answers || []).filter((_, i) => i !== aIndex);
      updated.questions[qIndex] = q;
      return updated;
    });
  };

  const handleCloseModal = () => {
    setModalIsOpen(false);
    setIsEditMode(false);
    setEditableFormData({ category: "", sub_category: "", year: "", questions: [] });
    setQuestions([]);
  };

  const handleSaveEdit = async () => {
    if (!selectedForm?.id) {
      toast.error("Invalid form selected");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        category: editableFormData.category,
        sub_category: editableFormData.sub_category,
        year: Number(editableFormData.year) || editableFormData.year,
        questions: (editableFormData.questions || []).map((q) => ({
          id: q.id,
          text: q.text,
          type: q.type,
          headerId: q.headerId ?? null,
          headerName: q.headerName ?? null,
          answers: (q.answers || []).map((a) => ({
            id: a.id,
            text: a.text,
            next_question_id: a.next_question_id ?? null,
          })),
        })),
      };

      const response = await fetch(`${SERVER_URL}/edit_form/${selectedForm.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Failed to save changes");
      }

      toast.success("Form updated successfully");
      handleCloseModal();
      fetchAllForms();
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Error saving form edits");
    } finally {
      setIsSaving(false);
    }
  };

  const renderQuestion = (question) => {
    return (
      <div
        key={question.id}
        className="mb-6 p-4 bg-white rounded-lg shadow-sm border border-gray-200"
      >
        <div className="grid grid-cols-1 gap-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full">
              <span className="text-blue-600 font-semibold">
                Q{question.id}
              </span>
            </div>
            <div className="flex-grow">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {question.question_text}
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Type:</span>{" "}
                  {question.question_type}
                </div>
                <div>
                  <span className="font-medium">Form ID:</span>{" "}
                  {question.form_id}
                </div>
                <div>
                  <span className="font-medium">Category:</span>{" "}
                  {question.category}
                </div>
                <div>
                  <span className="font-medium">Sub-Category:</span>{" "}
                  {question.sub_category}
                </div>
                <div>
                  <span className="font-medium">Year:</span> {question.year}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleDeleteForm = (formId) => {
    if (window.confirm("Are you sure you want to delete this form?")) {
      fetch(`${SERVER_URL}/delete_form/${formId}`, {
        method: "DELETE",
      })
        .then((response) => response.json())
        .then((data) => {
          toast.success("Form deleted successfully.");
          setForms(forms.filter((form) => form.id !== formId));
        })
        .catch((error) => {
          console.error("Error:", error);
          toast.error("Error deleting form.");
        });
    }
  };

  return (
    <div>
      <Outlet /> {/* Render child routes here */}
      {location.pathname === "/qualitative-assessment-dashboard" && ( // Render only on the parent route
        <>
          <div className="flex items-center gap-3">
            <FaArrowLeftLong
              className="cursor-pointer"
              onClick={() => {
                navigate("/overview");
              }}
            />
            <h3 className="font-semibold text-lg">
              Qualitative Assessment Dashboard
            </h3>
          </div>
          <div className="border-b mt-3 mb-4" />
          <div className="flex flex-col w-full p-5 mb-6 bg-white rounded-lg border border-gray-200">
            {/* Filters Section */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                {/* Country Filter */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-neutral-600">
                    Select Country
                  </label>
                  <select
                    name="country"
                    value={filters.country}
                    onChange={handleFilterChange}
                    className="block w-full px-5 py-3 text-base text-neutral-600 placeholder-gray-300 transition duration-500 ease-in-out transform border border-transparent rounded-lg bg-gray-50 focus:outline-none focus:border-transparent focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-300"
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Category Filter */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-neutral-600">
                    Select Category
                  </label>
                  <select
                    name="category"
                    value={filters.category}
                    onChange={handleFilterChange}
                    className="block w-full px-5 py-3 text-base text-neutral-600 placeholder-gray-300 transition duration-500 ease-in-out transform border border-transparent rounded-lg bg-gray-50 focus:outline-none focus:border-transparent focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-300"
                  >
                    <option value="">Select Category</option>
                    <option value="WASH">WASH</option>
                    <option value="Food Security">Food Security</option>
                    <option value="Education">Education</option>
                    <option value="Emergencies">Emergencies</option>
                    <option value="Health">Health</option>
                    <option value="Livelihood">Livelihood</option>
                  </select>
                </div>
                {/* Sub-Category Filter */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-neutral-600">
                    Select Sub-Category
                  </label>
                  <select
                    name="subCategory"
                    value={filters.subCategory}
                    onChange={handleFilterChange}
                    className="block w-full px-5 py-3 text-base text-neutral-600 placeholder-gray-300 transition duration-500 ease-in-out transform border border-transparent rounded-lg bg-gray-50 focus:outline-none focus:border-transparent focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-300"
                  >
                    <option value="">Select Sub-Category</option>
                    {filters.category === "WASH" && (
                      <>
                        <option value="WASH Committee">WASH Committee</option>
                        <option value="Community">Community</option>
                        <option value="Adolescent Girls">
                          Adolescent Girls
                        </option>
                        <option value="Health Officials">
                          Health Officials
                        </option>
                      </>
                    )}
                    {filters.category === "Food Security" && (
                      <>
                        <option value="Student">Student</option>
                        <option value="Food Distribution">
                          Food Distribution
                        </option>
                        <option value="Farmers">Farmers</option>
                      </>
                    )}
                    {filters.category === "Education" && (
                      <>
                        <option value="Principal">Principal</option>
                        <option value="Orphan Students">Orphan Students</option>
                        <option value="Teacher">Teacher</option>
                        <option value="Parent Guardian">Parent Guardian</option>
                      </>
                    )}
                    {filters.category === "Emergencies" && (
                      <>
                        <option value="EWS member v5">EWS member v5</option>
                        <option value="Community of Disaster hit areas">
                          Community of Disaster hit areas
                        </option>
                      </>
                    )}
                    {filters.category === "Health" && (
                      <>
                        <option value="Midwife">Midwife</option>
                        <option value="Community(Women)">
                          Community(Women)
                        </option>
                        <option value="Health Officials">
                          Health Officials
                        </option>
                        <option value="Community">Community</option>
                        <option value="Medical Camp Beneficiary">
                          Medical Camp Beneficiary
                        </option>
                      </>
                    )}
                    {filters.category === "Livelihood" && (
                      <>
                        <option value="Farmer">Farmer</option>
                        <option value="Trainee">Trainee</option>
                      </>
                    )}
                  </select>
                </div>
                {/* Year Filter */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-neutral-600">
                    Select Year
                  </label>
                  <select
                    name="year"
                    value={filters.year}
                    onChange={handleFilterChange}
                    className="block w-full px-5 py-3 text-base text-neutral-600 placeholder-gray-300 transition duration-500 ease-in-out transform border border-transparent rounded-lg bg-gray-50 focus:outline-none focus:border-transparent focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-300"
                  >
                    <option value="">Select Year</option>
                    <option value="2025">2025</option>
                  </select>
                </div>
                {/* Reset Button */}
                <div className="flex items-end">
                  <button
                    onClick={handleReset}
                    className="px-5 py-2.5 text-white bg-gray-400 hover:bg-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-300"
                  >
                    Reset Filters
                  </button>
                </div>
              </div>
            </div>
            {/* Create Form and Manage Page Buttons */}
            <div className="flex items-end mt-4 space-x-2">
              <button
                onClick={handleCreateForm}
                className="px-5 py-2.5 text-white bg-blue-500 hover:bg-blue-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-300"
              >
                Create Form
              </button>
              <button
                onClick={handleManagePage}
                className="px-5 py-2.5 text-white bg-green-500 hover:bg-green-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-300"
              >
                Manage Page
              </button>
              <button
                onClick={handleViewData}
                className="px-5 py-2.5 text-white bg-purple-500 hover:bg-purple-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-300"
              >
                View Data
              </button>
            </div>
          </div>
          {/* Published Forms Section */}
          <div className="mt-8">
            <h6 className="text-lg pb-2 font-semibold">Published Forms</h6>
            <div className="flex flex-col w-full p-5 mb-6 bg-white rounded-lg border border-gray-200">
              {isLoadingForms ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              ) : forms.length > 0 ? (
                <table className="min-w-full bg-white">
                  <thead>
                    <tr>
                      <th className="py-2 px-4 border-b border-gray-200 bg-gray-100 text-left text-sm font-semibold text-gray-700">
                        ID
                      </th>
                      <th className="py-2 px-4 border-b border-gray-200 bg-gray-100 text-left text-sm font-semibold text-gray-700">
                        Category
                      </th>
                      <th className="py-2 px-4 border-b border-gray-200 bg-gray-100 text-left text-sm font-semibold text-gray-700">
                        Sub-Category
                      </th>
                      <th className="py-2 px-4 border-b border-gray-200 bg-gray-100 text-left text-sm font-semibold text-gray-700">
                        Year
                      </th>
                      <th className="py-2 px-4 border-b border-gray-200 bg-gray-100 text-left text-sm font-semibold text-gray-700">
                        Status
                      </th>
                      <th className="py-2 px-4 border-b border-gray-200 bg-gray-100 text-left text-sm font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {forms.map((form) => (
                      <tr key={form.id} className="hover:bg-gray-50">
                        <td className="py-2 px-4 border-b border-gray-200">
                          {form.id}
                        </td>
                        <td className="py-2 px-4 border-b border-gray-200">
                          {form.category}
                        </td>
                        <td className="py-2 px-4 border-b border-gray-200">
                          {form.sub_category}
                        </td>
                        <td className="py-2 px-4 border-b border-gray-200">
                          {form.year}
                        </td>
                        <td className="py-2 px-4 border-b border-gray-200">
                          <span className="bg-green-200 text-green-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded">
                            Published
                          </span>
                        </td>
                        <td className="py-2 px-4 border-b border-gray-200">
                          <button
                            onClick={() => handleShowQuestionnaire(form)}
                            className="px-4 py-2 text-white bg-blue-500 hover:bg-blue-600 rounded-md mr-2 transition-colors"
                          >
                            Show Questionnaire
                          </button>
                          <button
                            onClick={() => handleEditForm(form)}
                            className="px-4 py-2 text-white bg-indigo-500 hover:bg-indigo-600 rounded-md mr-2 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleViewLogic(form)}
                            className="px-4 py-2 text-white bg-teal-600 hover:bg-teal-700 rounded-md mr-2 transition-colors"
                          >
                            View Logic
                          </button>
                          <button
                            onClick={() => handleDeleteForm(form.id)}
                            className="px-4 py-2 text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No forms available.{" "}
                  {filters.category && filters.subCategory && filters.year
                    ? "Try different filters."
                    : "Create a new form to get started."}
                </div>
              )}
            </div>
          </div>

          {/* Questionnaire / Edit Modal */}
          {modalIsOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6 border-b pb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800 mb-2">
                        {isEditMode ? "Edit Questionnaire" : "Questionnaire Details"}
                      </h2>
                      {!isEditMode ? (
                      <p className="text-gray-600">
                          {selectedForm?.category} - {selectedForm?.sub_category} ({selectedForm?.year})
                        </p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                            <select
                              value={editableFormData.category}
                              onChange={(e) => setEditableFormData((prev) => ({ ...prev, category: e.target.value, sub_category: "" }))}
                              className="block w-full px-3 py-2 border border-gray-300 rounded"
                            >
                              <option value="">Select Category</option>
                              {Object.keys(categories).map((cat) => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Sub-Category</label>
                            <select
                              value={editableFormData.sub_category}
                              onChange={(e) => setEditableFormData((prev) => ({ ...prev, sub_category: e.target.value }))}
                              className="block w-full px-3 py-2 border border-gray-300 rounded"
                              disabled={!editableFormData.category}
                            >
                              <option value="">Select Sub-Category</option>
                              {(editableFormData.category ? categories[editableFormData.category] : []).map((sub) => (
                                <option key={sub} value={sub}>{sub}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
                            <select
                              value={editableFormData.year}
                              onChange={(e) => setEditableFormData((prev) => ({ ...prev, year: e.target.value }))}
                              className="block w-full px-3 py-2 border border-gray-300 rounded"
                            >
                              <option value="">Select Year</option>
                              <option value="2026">2026</option>
                              <option value="2025">2025</option>
                       
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleCloseModal}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  {loading ? (
                    <div className="flex justify-center items-center h-64">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                  ) : !isEditMode ? (
                    <div className="space-y-6">
                      {(() => {
                        // group by headerName for display
                        const groups = new Map();
                        (questions || []).forEach((q) => {
                          const key = q.headerName || "__UNGROUPED__";
                          if (!groups.has(key)) groups.set(key, []);
                          groups.get(key).push(q);
                        });
                        const entries = Array.from(groups.entries());
                        return entries.map(([name, items], idx) => (
                          <div key={idx} className="space-y-3">
                            {name !== "__UNGROUPED__" && (
                              <h3 className="text-xl font-bold text-gray-800">{name}</h3>
                            )}
                            {items.map((question) => renderQuestion(question))}
                          </div>
                        ));
                      })()}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Simple header manager for edit mode */}
                      <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
                        <div className="flex items-center gap-2 flex-wrap mb-3">
                          {editHeaders.map((h, i) => (
                            <div key={i} className="group relative inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 shadow-sm">
                              <span className="text-xs font-medium">{h.name || (h.id ? `Header ${h.id}` : "Header")}</span>
                              <button
                                type="button"
                                onClick={() => removeEditHeader(h)}
                                className="w-5 h-5 flex items-center justify-center rounded-full bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-100"
                                title="Remove header"
                              >
                                ×
                              </button>
                              <button
                                type="button"
                                onClick={() => assignRemainingQuestionsToHeader(h)}
                                className="hidden group-hover:inline-flex text-[10px] ml-1 px-2 py-1 rounded-full bg-indigo-600 text-white hover:bg-indigo-700"
                                title="Tag all unassigned questions to this header"
                              >
                                Tag remaining
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newEditHeaderName}
                            onChange={(e) => setNewEditHeaderName(e.target.value)}
                            placeholder="Create new header"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded"
                          />
                          <button
                            type="button"
                            className="px-4 py-2 bg-indigo-600 text-white rounded"
                            onClick={() => {
                              const val = (newEditHeaderName || "").trim();
                              if (!val) return;
                              if (!editHeaders.some((h) => h.name === val)) {
                                setEditHeaders((prev) => [...prev, { id: Date.now(), name: val }]);
                              }
                              setNewEditHeaderName("");
                            }}
                          >
                            Add Header
                          </button>
                        </div>
                      </div>
                      {editableFormData.questions.map((q, qIndex) => (
                        <div key={qIndex} className="p-4 bg-gray-50 border border-gray-200 rounded">
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-sm text-gray-500">{q.id ? `Q${q.id}` : `New Q${qIndex + 1}`}</div>
                            <button
                              onClick={() => removeEditableQuestion(qIndex)}
                              className="text-red-600 hover:text-red-700 text-sm"
                            >
                              Remove Question
                            </button>
                          </div>
                          <div className="mb-3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Header</label>
                            <select
                              value={q.headerId ? String(q.headerId) : ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                const id = val ? Number(val) : null;
                                const hdr = id ? editHeaders.find((h) => String(h.id) === String(id)) : null;
                                setEditableFormData((prev) => {
                                  const updated = { ...prev };
                                  updated.questions = [...updated.questions];
                                  updated.questions[qIndex] = {
                                    ...updated.questions[qIndex],
                                    headerId: id,
                                    headerName: hdr ? hdr.name : null,
                                  };
                                  return updated;
                                });
                              }}
                              className="block w-full p-2 border border-gray-300 rounded"
                            >
                              <option value="">No Header</option>
                              {editHeaders.map((h, i) => (
                                <option key={i} value={String(h.id)}>
                                  {h.name || (h.id ? `Header ${h.id}` : "Header")}
                                </option>
                              ))}
                            </select>
                          </div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Question Text</label>
                          <textarea
                            value={q.text}
                            onChange={(e) => updateEditableQuestionText(qIndex, e.target.value)}
                            className="block w-full p-2 border border-gray-300 rounded mb-3"
                            rows={2}
                          />
                          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                          <select
                            value={q.type}
                            onChange={(e) => updateEditableQuestionType(qIndex, e.target.value)}
                            className="block w-full p-2 border border-gray-300 rounded mb-3"
                          >
                            <option value="text">Text</option>
                            <option value="checkbox">Checkbox</option>
                            <option value="radio">Radio</option>
                            <option value="date">Date</option>
                            <option value="time">Time</option>
                          </select>
                          {(q.type === "checkbox" || q.type === "radio") && (
                            <div className="space-y-2">
                              {(q.answers || []).map((a, aIndex) => (
                                <div key={aIndex} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-center">
                                  <input
                                    type="text"
                                    value={a.text}
                                    onChange={(e) => updateEditableAnswerText(qIndex, aIndex, e.target.value)}
                                    placeholder="Answer text"
                                    className="md:col-span-4 px-3 py-2 border border-gray-300 rounded"
                                  />
                                  <input
                                    type="number"
                                    value={a.next_question_id ?? ""}
                                    onChange={(e) => updateEditableAnswerNextId(qIndex, aIndex, e.target.value)}
                                    placeholder="Next Q ID"
                                    className="md:col-span-1 px-3 py-2 border border-gray-300 rounded"
                                  />
                                  <button
                                    onClick={() => removeEditableAnswer(qIndex, aIndex)}
                                    className="md:col-span-1 px-3 py-2 text-white bg-red-500 hover:bg-red-600 rounded"
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                              <button
                                onClick={() => addEditableAnswer(qIndex)}
                                className="px-4 py-2 text-white bg-blue-500 hover:bg-blue-600 rounded"
                              >
                                Add Answer
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      <button onClick={addEditableQuestion} className="px-5 py-2 text-white bg-green-600 hover:bg-green-700 rounded">
                        Add Question
                      </button>
                    </div>
                  )}

                  <div className="mt-6 flex justify-end space-x-2">
                    {isEditMode && (
                      <button
                        onClick={handleSaveEdit}
                        disabled={isSaving}
                        className="px-6 py-2 text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg transition-colors"
                      >
                        {isSaving ? "Saving..." : "Save"}
                      </button>
                    )}
                    <button
                      onClick={handleCloseModal}
                      className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Logic Viewer Modal */}
          {logicIsOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-stretch justify-stretch z-50">
              <div className="bg-white w-full h-full overflow-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4 border-b pb-3 sticky top-0 bg-white z-10">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800">Logic Viewer</h2>
                      <p className="text-gray-600">
                        {selectedForm?.category} - {selectedForm?.sub_category} ({selectedForm?.year})
                      </p>
                    </div>
                    <button onClick={() => setLogicIsOpen(false)} className="text-gray-500 hover:text-gray-700">✕</button>
                  </div>
                  {logicLoading ? (
                    <div className="flex justify-center items-center h-64">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
                    </div>
                  ) : (
                    <LogicViewer questions={logicQuestions} />
                  )}
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => setLogicIsOpen(false)}
                      className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default QualitativeAssessmentDashboard;
